import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  createSkillSchema,
  EXECUTOR_TYPES,
  SKILL_STATUS,
  type CreateSkillInput,
} from "@aos/shared";
import { MarkdownEditor } from "@/components/app/MarkdownEditor";
import { Label } from "@/components/ui/label";
import { FormShell } from "./FormShell";
import {
  NumberField,
  optionsFromEnum,
  SelectField,
  TextAreaField,
  TextField,
} from "./form";

export interface SkillFormProps {
  action: "create" | "edit";
}

/**
 * Create/edit form for a Skill. Validated against `createSkillSchema`; on edit
 * Refine hydrates the form from the fetched record. The skill body uses the
 * shared MarkdownEditor (controlled via RHF watch/setValue).
 */
export function SkillForm({ action }: SkillFormProps) {
  const form = useForm<CreateSkillInput>({
    resolver: zodResolver(createSkillSchema),
    refineCoreProps: { resource: "skills", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
    watch,
    setValue,
  } = form;

  const body = watch("body_markdown") ?? "";

  return (
    <FormShell
      title={action === "create" ? "New Skill" : "Edit Skill"}
      subtitle="Define the skill's contracts, body, and lifecycle metadata."
      resource="skills"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField form={form} name="name" label="Name" placeholder="Skill name" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField form={form} name="domain" label="Domain" placeholder="e.g. operations" />
        <TextField
          form={form}
          name="department"
          label="Department"
          placeholder="e.g. estimating"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="status"
          label="Status"
          options={optionsFromEnum(SKILL_STATUS)}
        />
        <SelectField
          form={form}
          name="allowed_executor_type"
          label="Allowed Executor"
          options={optionsFromEnum(EXECUTOR_TYPES)}
          allowEmpty
        />
      </div>
      <TextAreaField
        form={form}
        name="short_summary"
        label="Short Summary"
        placeholder="One-line summary of what this skill does"
      />
      <div className="space-y-1.5">
        <Label htmlFor="body_markdown">Body (Markdown)</Label>
        <MarkdownEditor
          value={body}
          onChange={(next) =>
            setValue("body_markdown", next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextAreaField
          form={form}
          name="input_contract"
          label="Input Contract"
          placeholder="JSON / description of expected inputs"
          rows={3}
        />
        <TextAreaField
          form={form}
          name="output_contract"
          label="Output Contract"
          placeholder="JSON / description of produced outputs"
          rows={3}
        />
      </div>
      <NumberField
        form={form}
        name="version"
        label="Version"
        placeholder="1"
      />
    </FormShell>
  );
}

export default SkillForm;
